/**
 * WebSocket Hook for Live Announcements
 * Connects to Django Channels WebSocket for real-time announcement notifications
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { tokenUtils } from '../lib/api';
import { toast } from './use-toast';

// WebSocket connection states
const WS_STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

// Configuration
const RECONNECT_INTERVAL = 5000; // 5 seconds
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Get WebSocket URL based on current environment
 */
const getWebSocketUrl = () => {
  const token = tokenUtils.getAccessToken();
  if (!token) return null;

  // Determine WebSocket protocol and host
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  
  // Empty REACT_APP_API_BASE_URL means "same origin": UAT builds it blank and
  // nginx proxies /ws/ to the backend, so the socket must target the page host.
  // Using || here sent every deployed browser to ws://localhost:8000 instead.
  const apiUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

  // Extract host from API URL, falling back to the page's own host
  let wsHost;
  if (!apiUrl) {
    wsHost = window.location.host;
  } else {
    try {
      const url = new URL(apiUrl);
      wsHost = url.host;
    } catch {
      wsHost = window.location.host;
    }
  }

  return `${protocol}//${wsHost}/ws/live-announcements/?token=${token}`;
};

/**
 * Hook for managing WebSocket connection to live announcements
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable the WebSocket connection
 * @param {Function} options.onNewAnnouncement - Callback when new announcement is received
 * @param {Function} options.shouldAccept - Optional predicate; return false to drop
 *   an announcement (no toast, no callback). The server broadcasts the public
 *   subcategories to every socket, so this is where a user who switched Results
 *   & Filings off has them filtered out.
 * @returns {Object} - { isConnected, connectionState, reconnect }
 */
export function useLiveAnnouncementSocket({ enabled = true, onNewAnnouncement, shouldAccept } = {}) {
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const [connectionState, setConnectionState] = useState('disconnected');

  /**
   * Show toast notification for new announcement
   */
  const showAnnouncementToast = useCallback((announcement) => {
    const { headline, company_name, category_name } = announcement;
    
    // Determine toast variant based on category
    let variant = 'info';
    if (category_name?.toLowerCase().includes('financial') || category_name?.toLowerCase().includes('result')) {
      variant = 'success';
    }

    toast({
      title: '📢 New Live Announcement',
      description: `${company_name}: ${headline || 'New announcement posted'}`,
      variant,
      duration: 8000, // Show for 8 seconds for important notifications
    });
  }, []);

  /**
   * Handle incoming WebSocket message
   */
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      if (data.type === 'new_announcement') {
        const announcement = data.announcement || data;

        // Drop announcements the user has opted out of before they surface.
        if (shouldAccept && !shouldAccept(announcement)) {
          return;
        }

        // Show toast notification
        showAnnouncementToast(announcement);

        // Call custom callback if provided
        if (onNewAnnouncement) {
          onNewAnnouncement(announcement);
        }
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }, [showAnnouncementToast, onNewAnnouncement, shouldAccept]);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    // Don't connect if not enabled or already connected
    if (!enabled) return;
    if (wsRef.current?.readyState === WS_STATES.OPEN) return;
    if (wsRef.current?.readyState === WS_STATES.CONNECTING) return;

    const url = getWebSocketUrl();
    if (!url) {
      console.log('No auth token available, skipping WebSocket connection');
      return;
    }

    try {
      setConnectionState('connecting');
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        console.log('Live Announcements WebSocket connected');
        setConnectionState('connected');
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = handleMessage;

      wsRef.current.onclose = (event) => {
        console.log('Live Announcements WebSocket closed:', event.code, event.reason);
        setConnectionState('disconnected');
        
        // Attempt reconnection if enabled and not intentionally closed
        if (enabled && event.code !== 1000 && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current += 1;
          console.log(`Attempting reconnection (${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, RECONNECT_INTERVAL);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Live Announcements WebSocket error:', error);
        setConnectionState('error');
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionState('error');
    }
  }, [enabled, handleMessage]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    // Clear any pending reconnection
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    
    // Close the WebSocket connection
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }
    
    setConnectionState('disconnected');
  }, []);

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    disconnect();
    setTimeout(connect, 100);
  }, [disconnect, connect]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    // Small delay to avoid race condition with React StrictMode and initial render
    let connectTimeout;
    if (enabled) {
      connectTimeout = setTimeout(() => {
        connect();
      }, 500);
    }

    return () => {
      if (connectTimeout) clearTimeout(connectTimeout);
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Reconnect when token changes (e.g., after refresh)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'access_token' && enabled) {
        reconnect();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [enabled, reconnect]);

  return {
    isConnected: connectionState === 'connected',
    connectionState,
    reconnect
  };
}

export default useLiveAnnouncementSocket;
